import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  formatBytes,
  formatEta,
  formatSpeed,
  nzbApi,
  type QueueItem,
  type QueueState,
} from "@/api/nzb";
import { ServerChips } from "@/components/ServerChips";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, Select } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

type Filter = "all" | "active" | "queued" | "paused";

const ACTIVE_STATES: QueueState[] = ["downloading", "fetching", "post_processing"];

export function NzbQueue() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<Filter>("all");
  const [uploadOpen, setUploadOpen] = useState(false);

  // Auto-refresh every 2s — matches the rustnzb demo cadence.
  const queue = useQuery({
    queryKey: ["nzb", "queue"],
    queryFn: nzbApi.queue.list,
    refetchInterval: 2000,
  });

  const status = useQuery({
    queryKey: ["nzb", "status"],
    queryFn: nzbApi.status,
    refetchInterval: 2000,
  });

  const categories = useQuery({
    queryKey: ["nzb", "categories"],
    queryFn: nzbApi.categories.list,
  });

  const filtered = useMemo(() => {
    const items = queue.data ?? [];
    if (filter === "all") return items;
    if (filter === "active")
      return items.filter((i) => ACTIVE_STATES.includes(i.state));
    if (filter === "queued") return items.filter((i) => i.state === "queued");
    if (filter === "paused") return items.filter((i) => i.state === "paused");
    return items;
  }, [queue.data, filter]);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["nzb", "queue"] });

  const onAction = async (
    label: string,
    fn: () => Promise<void>,
  ): Promise<void> => {
    try {
      await fn();
      invalidate();
    } catch (e) {
      toast(e instanceof Error ? e.message : `${label} failed`, "error");
    }
  };

  const pauseAll = () =>
    onAction("Pause all", async () => {
      await nzbApi.queue.pauseAll();
      qc.invalidateQueries({ queryKey: ["nzb", "status"] });
      toast("Queue paused", "info");
    });

  const resumeAll = () =>
    onAction("Resume all", async () => {
      await nzbApi.queue.resumeAll();
      qc.invalidateQueries({ queryKey: ["nzb", "status"] });
      toast("Queue resumed", "info");
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Queue"
        description={
          status.data
            ? `${formatSpeed(status.data.speed_bps)} • ${
                status.data.queue_count
              } in queue • max ${status.data.max_active} active${
                status.data.paused ? " • PAUSED" : ""
              }`
            : "Active and pending NZB downloads."
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={pauseAll}>
              Pause all
            </Button>
            <Button variant="secondary" onClick={resumeAll}>
              Resume all
            </Button>
            <Button onClick={() => setUploadOpen(true)}>Upload NZB</Button>
          </div>
        }
      />

      <FilterTabs value={filter} onChange={setFilter} counts={countsByFilter(queue.data ?? [])} />

      {queue.isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : queue.isError ? (
        <DownloaderOff message={queue.error instanceof Error ? queue.error.message : null} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={
            filter === "all"
              ? "Queue is empty"
              : `No ${filter} items`
          }
          description={
            filter === "all"
              ? "Upload an NZB to get started."
              : "Try a different filter."
          }
          action={
            filter === "all" ? (
              <Button onClick={() => setUploadOpen(true)}>Upload NZB</Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Mobile card list — the desktop table has 8 columns and a
              wide progress bar; on a phone it's a horizontal-scroll
              nightmare. Cards stack the same data. */}
          <ul className="space-y-2 md:hidden">
            {filtered.map((row) => (
              <QueueCard key={`mc-${row.id}`} row={row} onAction={onAction} />
            ))}
          </ul>
          <div className="hidden overflow-x-auto rounded border border-line md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="w-64 px-4 py-2 font-medium">Progress</th>
                  <th className="px-4 py-2 font-medium">Size</th>
                  <th className="px-4 py-2 font-medium">ETA</th>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 text-right font-medium">Priority</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <QueueRow
                    key={row.id}
                    row={row}
                    onAction={onAction}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        categories={(categories.data ?? []).map((c) => c.name)}
      />
    </div>
  );
}

function countsByFilter(items: QueueItem[]): Record<Filter, number> {
  return {
    all: items.length,
    active: items.filter((i) => ACTIVE_STATES.includes(i.state)).length,
    queued: items.filter((i) => i.state === "queued").length,
    paused: items.filter((i) => i.state === "paused").length,
  };
}

function FilterTabs({
  value,
  onChange,
  counts,
}: {
  value: Filter;
  onChange: (next: Filter) => void;
  counts: Record<Filter, number>;
}) {
  const tabs: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "active", label: "Active" },
    { id: "queued", label: "Queued" },
    { id: "paused", label: "Paused" },
  ];
  return (
    <div className="inline-flex rounded border border-line bg-surface/40 p-0.5 text-xs">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            "rounded px-3 py-1.5 transition",
            value === t.id
              ? "bg-accent/20 font-medium text-foreground"
              : "text-foreground/60 hover:text-foreground",
          )}
        >
          {t.label}
          <span className="ml-1.5 text-foreground/40">{counts[t.id]}</span>
        </button>
      ))}
    </div>
  );
}

function QueueRow({
  row,
  onAction,
}: {
  row: QueueItem;
  onAction: (label: string, fn: () => Promise<void>) => Promise<void>;
}) {
  const isPaused = row.state === "paused";
  const togglePause = () =>
    onAction(isPaused ? "Resume" : "Pause", () =>
      isPaused ? nzbApi.queue.resume(row.id) : nzbApi.queue.pause(row.id),
    );
  const remove = () => {
    if (!confirm(`Remove "${row.name}" from queue?`)) return;
    return onAction("Remove", () => nzbApi.queue.remove(row.id));
  };
  // Priority arrows: up = higher priority = lower numeric value (mirrors
  // SABnzbd / NZBGet semantics where 0 = normal and negative = lower).
  const bumpPriority = (delta: number) =>
    onAction("Set priority", () =>
      nzbApi.queue.setPriority(row.id, row.priority + delta),
    );

  const pct = Math.min(100, Math.max(0, Math.round((row.progress ?? 0) * 100)));

  return (
    <tr className="border-t border-line">
      <td className="max-w-md px-4 py-2 align-top">
        <div className="truncate font-medium" title={row.name}>
          {row.name}
        </div>
        {/* Per-server chips: tells the user at a glance whether their
            primary or fallback news server is carrying this download.
            Hidden until the engine has actually pulled an article. */}
        {row.servers && row.servers.length > 0 && (
          <ServerChips servers={row.servers} className="mt-1.5" />
        )}
      </td>
      <td className="px-4 py-2 text-foreground/70">{row.category ?? "—"}</td>
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded bg-muted">
            <div
              className={cn(
                "h-full transition-all",
                isPaused ? "bg-foreground/40" : "bg-accent",
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="w-10 text-right text-xs tabular-nums text-foreground/60">
            {pct}%
          </span>
        </div>
      </td>
      <td className="px-4 py-2 text-xs text-foreground/70 tabular-nums">
        {formatBytes(row.downloaded)} / {formatBytes(row.size)}
      </td>
      <td className="px-4 py-2 text-xs text-foreground/70 tabular-nums">
        {formatEta(row.eta_secs)}
      </td>
      <td className="px-4 py-2">
        <StateBadge state={row.state} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <span className="w-6 text-center text-xs tabular-nums text-foreground/60">
            {row.priority}
          </span>
          <button
            className="rounded border border-line px-1 text-xs hover:border-accent"
            title="Higher priority"
            onClick={() => bumpPriority(1)}
          >
            ▲
          </button>
          <button
            className="rounded border border-line px-1 text-xs hover:border-accent"
            title="Lower priority"
            onClick={() => bumpPriority(-1)}
          >
            ▼
          </button>
        </div>
      </td>
      <td className="space-x-2 px-4 py-2 text-right">
        <Button variant="secondary" size="sm" onClick={togglePause}>
          {isPaused ? "Resume" : "Pause"}
        </Button>
        <Button variant="danger" size="sm" onClick={remove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

function QueueCard({
  row,
  onAction,
}: {
  row: QueueItem;
  onAction: (label: string, fn: () => Promise<void>) => Promise<void>;
}) {
  const isPaused = row.state === "paused";
  const togglePause = () =>
    onAction(isPaused ? "Resume" : "Pause", () =>
      isPaused ? nzbApi.queue.resume(row.id) : nzbApi.queue.pause(row.id),
    );
  const remove = () => {
    if (!confirm(`Remove "${row.name}" from queue?`)) return;
    return onAction("Remove", () => nzbApi.queue.remove(row.id));
  };
  const bumpPriority = (delta: number) =>
    onAction("Set priority", () =>
      nzbApi.queue.setPriority(row.id, row.priority + delta),
    );
  const pct = Math.min(100, Math.max(0, Math.round((row.progress ?? 0) * 100)));
  return (
    <li className="rounded border border-line bg-surface/30 p-3">
      <div className="break-words font-medium text-sm leading-snug">
        {row.name}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded bg-muted">
          <div
            className={cn(
              "h-full transition-all",
              isPaused ? "bg-foreground/40" : "bg-accent",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="w-10 text-right text-xs tabular-nums text-foreground/60">
          {pct}%
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground/70">
        <StateBadge state={row.state} />
        {row.category && <span>{row.category}</span>}
        <span className="tabular-nums">
          {formatBytes(row.downloaded)} / {formatBytes(row.size)}
        </span>
        <span className="tabular-nums">{formatEta(row.eta_secs)}</span>
      </div>
      {row.servers && row.servers.length > 0 && (
        <ServerChips servers={row.servers} className="mt-2" />
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1">
          <span className="text-[11px] text-foreground/55">Priority</span>
          <span className="w-6 text-center text-xs tabular-nums">
            {row.priority}
          </span>
          <button
            className="rounded border border-line px-1.5 py-0.5 text-xs hover:border-accent"
            title="Higher priority"
            onClick={() => bumpPriority(1)}
            aria-label="Higher priority"
          >
            ▲
          </button>
          <button
            className="rounded border border-line px-1.5 py-0.5 text-xs hover:border-accent"
            title="Lower priority"
            onClick={() => bumpPriority(-1)}
            aria-label="Lower priority"
          >
            ▼
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={togglePause}>
            {isPaused ? "Resume" : "Pause"}
          </Button>
          <Button variant="danger" size="sm" onClick={remove}>
            Remove
          </Button>
        </div>
      </div>
    </li>
  );
}

function StateBadge({ state }: { state: QueueState }) {
  const tone =
    state === "downloading" || state === "fetching"
      ? "text-green-300"
      : state === "post_processing"
        ? "text-blue-300"
        : state === "paused"
          ? "text-yellow-300"
          : state === "queued"
            ? "text-foreground/60"
            : "text-foreground/40";
  const label = state === "post_processing" ? "post-proc" : state;
  return (
    <span className={cn("rounded bg-surface-2/60 px-1.5 py-0.5 text-[10px] uppercase", tone)}>
      {label}
    </span>
  );
}

function DownloaderOff({ message }: { message: string | null }) {
  return (
    <EmptyState
      title="NZB downloader is unavailable"
      description={
        message
          ? `Backend said: ${message}`
          : "Check that the embedded downloader is enabled in your config."
      }
    />
  );
}

// -----------------------------------------------------------------------------
// Upload modal
// -----------------------------------------------------------------------------

function UploadModal({
  open,
  onClose,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  categories: string[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      setFile(null);
      setCategory("");
      setPriority(0);
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast("Pick an NZB file first", "error");
      return;
    }
    setSubmitting(true);
    try {
      await nzbApi.queue.upload(file, {
        category: category || null,
        priority,
      });
      toast(`${file.name} added to queue`, "success");
      qc.invalidateQueries({ queryKey: ["nzb", "queue"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Upload failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Upload NZB" width="md">
      <form onSubmit={submit} className="space-y-4">
        <Field label="NZB file" hint="Select a .nzb file from your machine.">
          <input
            type="file"
            accept=".nzb,application/x-nzb"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-accent file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:opacity-90"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category" hint="Optional.">
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">— none —</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Priority" hint="Higher = sooner.">
            <Select
              value={String(priority)}
              onChange={(e) => setPriority(Number(e.target.value))}
            >
              <option value="-1">Low</option>
              <option value="0">Normal</option>
              <option value="1">High</option>
              <option value="2">Force</option>
            </Select>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Upload
          </Button>
        </div>
      </form>
    </Modal>
  );
}
